"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { assets } from "@/content/assets";
import { site } from "@/content/site";
import {
  getNavLightText,
  type NavAccentContext,
} from "@/components/layout/nav-types";
import { cn } from "@/lib/utils";

interface NavLogoProps extends NavAccentContext {
  isScrolled: boolean;
  onNavigate?: () => void;
}

export function NavLogo({ isScrolled, onNavigate, ...ctx }: NavLogoProps) {
  const reduced = useReducedMotion();
  const lightText = getNavLightText(ctx, isScrolled);

  return (
    <Link
      href="/"
      className={cn(
        "flex min-w-0 flex-1 items-center transition-[gap] duration-[450ms] lg:flex-initial lg:shrink-0",
        isScrolled ? "gap-1.5 sm:gap-2" : "gap-1.5 sm:gap-2.5 md:gap-3"
      )}
      style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
      onClick={onNavigate}
    >
      <motion.span
        whileHover={
          reduced
            ? undefined
            : {
                scale: 1.05,
                rotate: -2,
                boxShadow: "0 0 0 4px rgba(45, 122, 74, 0.15)",
              }
        }
        transition={{ type: "spring", stiffness: 400, damping: 22 }}
        className="inline-flex shrink-0 rounded-full"
      >
        <Image
          src={assets.logo}
          alt={site.shortName}
          width={44}
          height={44}
          className={cn(
            "rounded-full object-contain transition-[width,height] duration-[450ms]",
            isScrolled
              ? "h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10"
              : "h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12"
          )}
          style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
          priority
        />
      </motion.span>
      <div className="min-w-0 flex-1 leading-tight">
        <p
          className={cn(
            "max-w-full truncate font-bold leading-[1.2] transition-[font-size] duration-[450ms]",
            isScrolled
              ? "text-[8px] sm:text-[9px] md:text-xs xl:text-sm"
              : "text-[9px] sm:text-[10px] md:text-sm xl:text-base",
            lightText ? "text-white" : "text-gray-900"
          )}
          style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
        >
          <span className="sm:hidden">{site.shortName}</span>
          <span className="hidden sm:inline">{site.name}</span>
        </p>
        <p
          className={cn(
            "mt-0.5 hidden max-w-full truncate leading-[1.2] transition-[font-size] duration-[450ms] sm:block",
            isScrolled
              ? "text-[6px] sm:text-[7px] md:text-[9px] xl:text-[10px]"
              : "text-[7px] sm:text-[8px] md:text-[10px] xl:text-xs",
            lightText ? "text-white/75" : "text-gray-500"
          )}
          style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
        >
          {site.legalStatus}
        </p>
      </div>
    </Link>
  );
}
